import React from 'react';
import PropTypes from 'prop-types';
import { DateRangePicker } from 'react-dates';
import MonthRangePicker from 'SF/date/month-picker';

import momentPropTypes from 'react-moment-proptypes';
import { assign, isFunction } from 'lodash';

import 'global/react-dates-overrides.scss';

export const momentsEqual = (a, b) => {
  if (!a && !b) {
    return true;
  }

  return a && b && a.isSame(b);
};

const isEmptyDateRange = (start, end) => !start && !end;

const isDateValid = date => date && date.isValid();

export default class DateRange extends React.Component {
  static dateRangeDomId = 0;

  static propTypes = {
    startDate: momentPropTypes.momentObj,
    endDate: momentPropTypes.momentObj,
    screenReaderInputMessage: PropTypes.string.isRequired,
    disabled: PropTypes.bool,

    granularity: PropTypes.oneOf(['day', 'month']).isRequired,

    numberOfMonths: PropTypes.number,
    numberOfYears: PropTypes.number,

    onDatesChange: PropTypes.func.isRequired,
    onInvalidChange: PropTypes.func,

    acceptIncompleteRange: PropTypes.bool
  };

  static defaultProps = {
    numberOfMonths: 1,
    numberOfYears: 1,
    granularity: 'day',

    screenReaderInputMessage: 'Select a date range'
  };

  isDateKnownInvalid = false;

  constructor(props) {
    super(props);

    this.dateRangeDomId = ++DateRange.dateRangeDomId;

    // needed for DateRangePicker
    this.state = {
      focusedInput: null,
      startDate: props.startDate,
      endDate: props.endDate
    };
  }

  get startDateId() {
    return `dateRange-startDate${this.dateRangeDomId}`;
  }

  get endDateId() {
    return `dateRange-endDate${this.dateRangeDomId}`;
  }

  get partialDateInput() {
    return isDateValid(this.state.startDate) !== isDateValid(this.state.endDate);
  }

  UNSAFE_componentWillReceiveProps({startDate, endDate}) {
    if (isEmptyDateRange(startDate, endDate) && this.partialDateInput) {
      return;
    }

    if (momentsEqual(this.state.startDate, startDate) && momentsEqual(this.state.endDate, endDate)) {
      return;
    }

    this.setState({startDate, endDate});
  }

  handleDatesChange = (newDateRange) => {
    this.setState(newDateRange, () => this.updateDates(newDateRange));
  };

  updateDates(newDateRange) {
    const startValid = isDateValid(newDateRange.startDate);
    const endValid = isDateValid(newDateRange.endDate);

    this.setDateValidVisually(this.startDateId, startValid);
    this.setDateValidVisually(this.endDateId, endValid);

    if (this.props.acceptIncompleteRange || (startValid && endValid)) {
      this.isDateKnownInvalid = false;
      this.props.onDatesChange(newDateRange);
      return;
    }

    if (this.isDateKnownInvalid || !isFunction(this.props.onInvalidChange)) {
      return;
    }

    this.isDateKnownInvalid = true;
    this.props.onInvalidChange();
  }

  setDateValidVisually(dateId, valid) {
    const el = document.getElementById(dateId);
    const parentClassList = el.parentElement.classList;
    if (valid) {
      if (parentClassList.contains('error')) {
        parentClassList.remove('error');
      }
      if (el.hasAttribute('aria-invalid')) {
        el.removeAttribute('aria-invalid');
      }
    } else {
      parentClassList.add('error');
      el.setAttribute('aria-invalid', 'true');
    }
  }

  render() {
    const TimeRangePicker = this.monthPicker ? MonthRangePicker : DateRangePicker;
    return <TimeRangePicker {...this.timeRangePickerProps} />
  }

  get monthPicker() {
    return this.props.granularity === 'month';
  }

  get pickerGranularity() {
    if (this.monthPicker) {
      return {numberOfYears: this.props.numberOfYears};
    } else {
      return {numberOfMonths: this.props.numberOfMonths};
    }
  }

  onFocusChange = focusedInput => this.setState({focusedInput});

  get timeRangePickerProps() {
    return assign({
      startDate: this.state.startDate,
      endDate: this.state.endDate,
      startDateId: this.startDateId,
      endDateId: this.endDateId,
      disabled: this.props.disabled,
      onDatesChange: this.handleDatesChange,
      focusedInput: this.state.focusedInput,
      onFocusChange: this.onFocusChange,

      isOutsideRange: () => false, /* allow all days, including past days */
      screenReaderInputMessage: this.props.screenReaderInputMessage
    }, this.pickerGranularity);
  }
}
